import jsPDF from "jspdf";
import "jspdf-autotable";
import type { RowInput } from "jspdf-autotable";
import dayjs from "dayjs";
import type {
  AttributeOption,
  Equipment,
  EquipmentAttributeDefinition,
  EquipmentFilters,
  EquipmentModel,
  EquipmentPrefilter,
  EquipmentSearchRequest,
} from "../api/equipment/Equipment.typed";

// Backend caps search at 100 per page, so paginate.
const EXPORT_PAGE_SIZE = 100;

interface SearchFetcher {
  (req: EquipmentSearchRequest): Promise<{ equipments: Equipment[]; total: number; pages: number }>;
}

interface FetchAllArgs {
  modelId: number;
  filters: EquipmentFilters | undefined;
  prefilter: EquipmentPrefilter | null;
  sortBy: string | undefined;
  sortOrder: "ASC" | "DESC" | undefined;
  search: SearchFetcher;
  onProgress?: (loaded: number, total: number) => void;
}

export async function fetchAllFilteredEquipments({
  modelId,
  filters,
  prefilter,
  sortBy,
  sortOrder,
  search,
  onProgress,
}: FetchAllArgs): Promise<Equipment[]> {
  const baseRequest = (page: number): EquipmentSearchRequest => ({
    ModelID: modelId,
    Page: page,
    Limit: EXPORT_PAGE_SIZE,
    PrefilterID: prefilter?.ID,
    Filters: filters && filters.Criteria.length > 0 ? filters : undefined,
    ...(sortBy || sortOrder ? { SortBy: sortBy || "", SortOrder: sortOrder || "ASC" } : {}),
  });

  const first = await search(baseRequest(1));
  const all: Equipment[] = [...first.equipments];
  const total = first.total;
  const totalPages = first.pages || Math.ceil(total / EXPORT_PAGE_SIZE);
  onProgress?.(all.length, total);

  for (let page = 2; page <= totalPages; page++) {
    const batch = await search(baseRequest(page));
    all.push(...batch.equipments);
    onProgress?.(all.length, total);
  }

  return all;
}

export function formatEquipmentAttributeForExport(
  value: unknown,
  attr: EquipmentAttributeDefinition,
): string {
  if (value === null || value === undefined || value === "") return "";

  if (attr.Type === "date") {
    const d = dayjs(String(value));

    return d.isValid() ? d.format("DD/MM/YYYY") : String(value);
  }

  if (attr.Type === "list") {
    const options = attr.Options || [];
    const findLabel = (raw: unknown) => {
      const key = String(raw);
      const opt = options.find((o: AttributeOption) => o.ID === key || o.Label === key);

      return opt ? opt.Label : key;
    };
    if (Array.isArray(value)) return value.map(findLabel).join(", ");

    return findLabel(value);
  }

  if (attr.Type === "comments") {
    if (Array.isArray(value)) return `${value.length} commentaire${value.length > 1 ? "s" : ""}`;

    return "";
  }

  if (typeof value === "boolean") return value ? "Oui" : "Non";
  if (typeof value === "number") return String(value);
  if (typeof value === "object") {
    try {
      return JSON.stringify(value);
    } catch {
      return "";
    }
  }

  return String(value);
}

function escapeCSVField(raw: string): string {
  if (raw.includes(";") || raw.includes('"') || raw.includes("\n") || raw.includes("\r")) {
    return `"${raw.replace(/"/g, '""')}"`;
  }

  return raw;
}

export function buildEquipmentsCSV(model: EquipmentModel, equipments: Equipment[]): Blob {
  const attributes = model.Attributes || [];
  const header = ["ID", ...attributes.map((a) => a.Label), "Créé le", "Modifié le"];
  const lines: string[] = [header.map(escapeCSVField).join(";")];

  for (const eq of equipments) {
    const row: string[] = [eq.ID ? String(eq.ID) : ""];
    for (const attr of attributes) {
      row.push(formatEquipmentAttributeForExport(eq.Attributes?.[attr.ID], attr));
    }
    row.push(eq.CreatedAt ? dayjs(eq.CreatedAt).format("DD/MM/YYYY HH:mm") : "");
    row.push(eq.UpdatedAt ? dayjs(eq.UpdatedAt).format("DD/MM/YYYY HH:mm") : "");
    lines.push(row.map(escapeCSVField).join(";"));
  }

  // UTF-8 BOM so Excel detects encoding.
  return new Blob(["\uFEFF" + lines.join("\n")], { type: "text/csv;charset=utf-8" });
}

interface BuildPDFOptions {
  filtersSummary?: string;
}

export function buildEquipmentsPDF(
  model: EquipmentModel,
  equipments: Equipment[],
  options: BuildPDFOptions = {},
): Blob {
  const attributes = model.Attributes || [];
  // Landscape A4 to fit many attribute columns.
  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });

  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 8;
  const now = dayjs().format("DD/MM/YYYY HH:mm");

  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.text(model.Name || "Équipements", margin, 12);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(110);
  const subtitle = `${equipments.length} équipement${equipments.length > 1 ? "s" : ""} • Exporté le ${now}`;
  doc.text(subtitle, margin, 17);
  if (options.filtersSummary) {
    doc.text(`Filtres : ${options.filtersSummary}`, margin, 22);
  }

  const head: RowInput[] = [["ID", ...attributes.map((a) => a.Label)]];
  const body: RowInput[] = equipments.map((eq) => {
    const cells: string[] = [eq.ID ? String(eq.ID) : ""];
    for (const attr of attributes) {
      cells.push(formatEquipmentAttributeForExport(eq.Attributes?.[attr.ID], attr));
    }

    return cells;
  });

  (doc as any).autoTable({
    head,
    body,
    startY: options.filtersSummary ? 26 : 22,
    margin: { left: margin, right: margin },
    styles: {
      fontSize: 7.5,
      cellPadding: 1.5,
      overflow: "linebreak",
      valign: "middle",
      textColor: [40, 40, 40],
      lineColor: [220, 220, 220],
      lineWidth: 0.1,
    },
    headStyles: {
      fillColor: [33, 33, 33],
      textColor: [240, 240, 240],
      fontStyle: "bold",
      fontSize: 8,
    },
    alternateRowStyles: { fillColor: [248, 248, 250] },
    columnStyles: {
      0: {
        cellWidth: 48,
        textColor: [120, 120, 120],
        font: "courier",
        fontSize: 6.5,
      },
    },
    tableWidth: pageWidth - margin * 2,
    didDrawPage: (data: { pageNumber: number }) => {
      const pageHeight = doc.internal.pageSize.getHeight();
      doc.setFontSize(8);
      doc.setTextColor(150);
      doc.text(`${model.Name || "Équipements"} — page ${data.pageNumber}`, margin, pageHeight - 5);
    },
  });

  return doc.output("blob");
}
